import * as React from "react";
import { stories as ci, releases as Bn, videoReleases } from "./data/catalog.js";
import { VideoPreviews } from "./VideoPreviews.jsx";
import {
  readRoute,
  ACCESS_STORAGE_KEY,
  allComics,
  getFreePageCount,
  longStoryIds,
  isRecentRelease,
  HOME_TITLE,
  HOME_DESCRIPTION,
  SITE_URL,
  setMeta,
  setCanonical,
  getStoredPassword,
  PREFETCH_COUNT,
  requiresProtectedSource,
  protectedPageSource,
  pageCacheKey,
  hasCachedPage,
  COMIC_API,
  cachePage,
  AGE_GATE_LOGO,
  pushRoute,
  clampPage,
  isPageLocked,
  PASSWORD_STORAGE_KEY,
  socialLinks,
  chapterReleases,
  miniStories,
  lockedImageUrl,
  publicPageUrl,
} from "./core.js";
import {
  LongStories,
  ComicGrid,
  Footer,
  QuickNavigation,
  ProtectedPage,
  VerticalPage,
} from "./components.jsx";
export function App() {
  const [route, setRoute] = React.useState(() => readRoute()),
    [hoverSide, setHoverSide] = React.useState(null),
    [zoom, setZoom] = React.useState(1),
    [nowMs, setNowMs] = React.useState(() => Date.now()),
    [hasFullAccess, setHasFullAccess] = React.useState(() =>
      typeof window > "u"
        ? !1
        : window.localStorage.getItem(ACCESS_STORAGE_KEY) === "true",
    ),
    [theme, setTheme] = React.useState(() => {
      if (typeof window > "u") return "light";
      const j = window.localStorage.getItem("theme");
      return j === "light" || j === "dark"
        ? j
        : window.matchMedia &&
            window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light";
    }),
    [accessModalOpen, setAccessModalOpen] = React.useState(!1),
    [passwordInput, setPasswordInput] = React.useState(""),
    [accessError, setAccessError] = React.useState(null),
    verticalReaderRef = React.useRef(null),
    [ageConfirmed, setAgeConfirmed] = React.useState(() =>
      typeof window > "u"
        ? !1
        : window.localStorage.getItem("age_confirmed_18") === "true",
    );
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [route.comicId, route.activeView]);
  (React.useEffect(() => {
    const j = () => {
      setRoute(readRoute());
    };
    return (
      window.addEventListener("popstate", j),
      () => {
        window.removeEventListener("popstate", j);
      }
    );
  }, []),
    React.useEffect(() => {
      ((document.body.dataset.theme = theme),
        window.localStorage.setItem("theme", theme));
    }, [theme]));
  const comic = React.useMemo(
      () =>
        route.comicId
          ? (allComics.find((j) => j.id === route.comicId) ?? null)
          : null,
      [route.comicId],
    ),
    freePageCount = comic ? getFreePageCount(comic) : null,
    isLongStory = comic != null && longStoryIds.has(comic.id),
    hasPremiumPages =
      comic != null && freePageCount != null && freePageCount < comic.pages,
    isFreeMode = hasPremiumPages && !hasFullAccess,
    isHome = route.activeView === "home",
    isLegal = route.activeView === "legal",
    hasScheduledRelease = Bn.some((j) => !j.isReleased && !!j.releaseAt),
    hasRecentRelease = Bn.some((j) => isRecentRelease(j.releasedAt, nowMs));
  (React.useEffect(() => {
    document.body.dataset.page = isHome ? "home" : "reader";
  }, [isHome]),
    React.useEffect(() => {
      const j = isLegal
          ? "Legal notice | EKYNOXE"
          : comic
            ? `${comic.name} - page ${route.page} | EKYNOXE comics`
            : HOME_TITLE,
        G = isLegal
          ? "Legal notice and copyright information for EKYNOXE."
          : comic
            ? `Read the EKYNOXE comic ${comic.name} online. Free comic preview pages and premium access are available on ekynoxe.art.`
            : HOME_DESCRIPTION,
        de = comic
          ? `${SITE_URL}/?comic=${encodeURIComponent(comic.id)}&page=${route.page}&mode=${route.orientation}`
          : isLegal
            ? `${SITE_URL}/?legal=1&mode=${route.orientation}`
            : `${SITE_URL}/?mode=${route.orientation}`;
      ((document.title = j),
        setMeta("name", "description", G),
        setMeta("property", "og:title", j),
        setMeta("property", "og:description", G),
        setMeta("property", "og:url", de),
        setMeta("name", "twitter:title", j),
        setMeta("name", "twitter:description", G),
        setCanonical(de));
    }, [comic, isLegal, route.orientation, route.page]),
    React.useEffect(() => {
      if (!isHome || (!hasScheduledRelease && !hasRecentRelease)) return;
      const j = window.setInterval(() => {
        setNowMs(Date.now());
      }, 1e3);
      return () => {
        window.clearInterval(j);
      };
    }, [hasRecentRelease, hasScheduledRelease, isHome]),
    React.useEffect(() => {
      if (!comic || route.orientation !== "vertical") return;
      verticalReaderRef.current
        ?.querySelector(`[data-page="${route.page}"]`)
        ?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
    }, [comic, route.orientation, route.page]),
    React.useEffect(() => {
      if (!hasFullAccess || !comic || route.orientation === "vertical") return;
      const j = getStoredPassword();
      if (!j) return;
      const G = new AbortController(),
        de = getFreePageCount(comic) ?? 0,
        Ye = Math.max(route.page + 1, de + 1),
        Mt = Math.min(comic.pages, Ye + PREFETCH_COUNT - 1);
      for (let Wt = Ye; Wt <= Mt; Wt += 1) {
        if (!requiresProtectedSource(comic, Wt)) continue;
        const Be = protectedPageSource(comic, Wt),
          Xn = pageCacheKey(Be.comicId, Be.page);
        hasCachedPage(Xn) ||
          (async () => {
            try {
              const Xa = await fetch(
                `${COMIC_API}?comicId=${encodeURIComponent(Be.comicId)}&page=${encodeURIComponent(String(Be.page))}`,
                {
                  method: "GET",
                  headers: {
                    "X-Comic-Password": j,
                  },
                  signal: G.signal,
                },
              );
              if (!Xa.ok) return;
              const Qn = await Xa.blob(),
                Ft = URL.createObjectURL(Qn);
              cachePage(Xn, Ft, 0);
            } catch {}
          })();
      }
      return () => {
        G.abort();
      };
    }, [hasFullAccess, comic, route.page, route.orientation]));
  const confirmAge = () => {
      (setAgeConfirmed(!0),
        typeof window < "u" &&
          window.localStorage.setItem("age_confirmed_18", "true"));
    },
    leaveSite = () => {
      typeof window < "u" && (window.location.href = "https://www.google.com");
    },
    ageGate = !ageConfirmed && (
      <div className="age-gate">
        <div className="age-gate-card">
          <img src={AGE_GATE_LOGO} alt="Site logo" className="age-gate-logo" />
          <h1 className="age-gate-title">{"Are you over 18?"}</h1>
          <p className="age-gate-text">
            {
              "This site contains sexual content and nudity. Confirm your age to continue."
            }
          </p>
          <p className="age-gate-text">
            {
              "All characters shown in these comics are 18 years or older, and all situations are fully consensual."
            }
          </p>
          <div className="age-gate-actions">
            <button
              type="button"
              className="button age-gate-button-primary"
              onClick={confirmAge}
            >
              {"Yes, I am over 18"}
            </button>
            <button
              type="button"
              className="button button-secondary age-gate-button-secondary"
              onClick={leaveSite}
            >
              {"No"}
            </button>
          </div>
          <p className="age-gate-footnote">
            {
              "We use cookies to personalise content and secure the site. By confirming your age, you agree to our use of cookies."
            }
          </p>
        </div>
      </div>
    ),
    openComic = (j) => {
      allComics.find((de) => de.id === j) &&
        setRoute((de) => {
          const Ye = {
            comicId: j,
            page: 1,
            orientation: de.orientation,
            activeView: "reader",
          };
          return (pushRoute(Ye), Ye);
        });
    },
    goHome = () => {
      setRoute((j) => {
        const G = {
          comicId: null,
          page: 1,
          orientation: j.orientation,
          activeView: "home",
        };
        return (pushRoute(G), G);
      });
    },
    openLegal = () => {
      setRoute((j) => {
        if (j.activeView === "legal") return j;
        const G = {
          comicId: null,
          page: 1,
          orientation: j.orientation,
          activeView: "legal",
        };
        return (pushRoute(G), G);
      });
    },
    goToPage = (j) => {
      if (!comic) return !1;
      const G = clampPage(j, comic.pages);
      return isPageLocked(comic, G, hasFullAccess)
        ? (setAccessError(null), setAccessModalOpen(!0), !1)
        : (setRoute((de) => {
            if (!de.comicId) return de;
            const Ye = {
              ...de,
              page: G,
              activeView: "reader",
            };
            return (pushRoute(Ye), Ye);
          }),
          !0);
    },
    navigatePage = (j) => {
      if (!comic) return;
      let G = route.page;
      (j === "next"
        ? (G = route.page + 1)
        : j === "prev"
          ? (G = route.page - 1)
          : (G = j),
        goToPage(G));
    },
    setOrientation = (j) => {
      setRoute((G) => {
        if (G.orientation === j) return G;
        const de = {
          ...G,
          orientation: j,
        };
        return (pushRoute(de), de);
      });
    },
    changeZoom = (j) => {
      setZoom((G) =>
        Math.min(1.3, Math.max(0.7, parseFloat((G + j).toFixed(2)))),
      );
    },
    toggleTheme = () => {
      setTheme((j) => (j === "light" ? "dark" : "light"));
    },
    openAccess = () => {
      (setAccessError(null), setAccessModalOpen(!0));
    },
    closeAccess = () => {
      setAccessModalOpen(!1);
    },
    onPasswordChange = (j) => {
      setPasswordInput(j.target.value);
    },
    onUnauthorized = () => {
      (setHasFullAccess(!1),
        typeof window < "u" &&
          (window.localStorage.removeItem(ACCESS_STORAGE_KEY),
          window.localStorage.removeItem(PASSWORD_STORAGE_KEY)),
        setAccessError(
          "Your access has expired. Please enter the new password from Fansly.",
        ),
        setAccessModalOpen(!0));
    },
    submitPassword = async (j) => {
      j.preventDefault();
      const G = passwordInput.trim();
      if (!G) {
        setAccessError("Please enter the password.");
        return;
      }
      try {
        if (
          !(
            await fetch(`${COMIC_API}?test=1`, {
              method: "GET",
              headers: {
                "X-Comic-Password": G,
              },
            })
          ).ok
        ) {
          setAccessError("Incorrect code, please try again.");
          return;
        }
      } catch {
        setAccessError("Unable to verify password. Please try again.");
        return;
      }
      (setHasFullAccess(!0),
        window.localStorage.setItem(ACCESS_STORAGE_KEY, "true"),
        window.localStorage.setItem(PASSWORD_STORAGE_KEY, G),
        setAccessError(null),
        setAccessModalOpen(!1));
    };
  (React.useEffect(() => {
    if (!hasFullAccess) return;
    getStoredPassword() || onUnauthorized();
  }, [hasFullAccess]),
    React.useEffect(() => {
      const j = (G) => {
        if (accessModalOpen || !ageConfirmed || G.target?.closest?.('input, textarea, select, [contenteditable="true"], video, [data-video-player]')) return;
        !comic ||
          route.orientation !== "horizontal" ||
          (G.key === "ArrowRight"
            ? (G.preventDefault(), navigatePage("next"))
            : G.key === "ArrowLeft" &&
              (G.preventDefault(), navigatePage("prev")));
      };
      return (
        window.addEventListener("keydown", j),
        () => {
          window.removeEventListener("keydown", j);
        }
      );
    }, [comic, navigatePage, route.orientation, accessModalOpen, ageConfirmed]));
  const LegalNotice = () => (
    <main className="app-main app-main--legal">
      <section className="legal-notice">
        <h1 className="legal-title">{"Legal Notice"}</h1>
        <section className="legal-section">
          <h2 className="legal-section-title">{"1. Website Publisher"}</h2>
          <p className="legal-text">
            {"This website is published and operated by:"}
          </p>
          <p className="legal-text">
            {"Pseudonym: Ekynoxe"}
            <br />
            {"Contact email: ekynoxe@proton.me"}
          </p>
          <p className="legal-text">
            {
              "This is a personal website operated under a pseudonym. No physical business address is provided because no in-person services are offered."
            }
          </p>
        </section>
        <section className="legal-section">
          <h2 className="legal-section-title">{"2. Hosting Provider"}</h2>
          <p className="legal-text">
            {"The website is hosted by:"}
            <br />
            {"Cloudflare, Inc."}
            <br />
            {"San Francisco, USA"}
          </p>
        </section>
        <section className="legal-section">
          <h2 className="legal-section-title">
            {"3. Nature of Content and Age Restriction (18+)"}
          </h2>
          <p className="legal-text">
            {
              "This website contains fictional adult content intended exclusively for persons who are at least 18 years old (or the legal age of majority in their jurisdiction, if higher). By accessing this website, you confirm that you meet the applicable legal age requirement and that viewing such content is lawful in your country or region."
            }
          </p>
          <p className="legal-text">
            {
              "If you are under 18 years old (or under the age of majority in your jurisdiction), you must not access this website."
            }
          </p>
        </section>
        <section className="legal-section">
          <h2 className="legal-section-title">
            {"4. Password-Protected Content"}
          </h2>
          <p className="legal-text">
            {
              "Some sections of the website may be protected by a password. Passwords are personal and must not be shared with third parties. Access to password-protected areas is intended solely for adults and remains subject to all conditions set out in this Legal Notice."
            }
          </p>
        </section>
        <section className="legal-section">
          <h2 className="legal-section-title">
            {"5. External Services (Fansly)"}
          </h2>
          <p className="legal-text">
            {
              "Payments, age verification, and subscriptions relating to the content of this website are handled externally via the service Fansly. No payments are processed on this website itself, and no user accounts are created or managed here."
            }
          </p>
          <p className="legal-text">
            {
              "When you use Fansly or any other external service, you are subject to their own terms, conditions, and privacy policy. This website has no control over and assumes no responsibility for the content, practices, or data processing activities of those external services."
            }
          </p>
        </section>
        <section className="legal-section">
          <h2 className="legal-section-title">{"6. Liability Limitation"}</h2>
          <p className="legal-text">
            {
              'The content provided on this website is for personal entertainment purposes only and is offered "as is," without any guarantees as to accuracy, completeness, or suitability for a particular purpose.'
            }
          </p>
          <p className="legal-text">
            {
              "To the maximum extent permitted by applicable law, the publisher (under the pseudonym Ekynoxe) shall not be held liable for any direct or indirect damage, loss, or consequences arising from access to or use of this website, inability to access or use this website, reliance on any content displayed on this website, or use of third-party services such as Fansly. Each user remains solely responsible for ensuring that accessing this website and related content is lawful in their jurisdiction."
            }
          </p>
        </section>
        <section className="legal-section">
          <h2 className="legal-section-title">
            {"7. Privacy and Data Protection"}
          </h2>
          <p className="legal-text">
            {
              "This website does not use tracking cookies, analytics tools, or advertising trackers. No user accounts are created on this website, and no payment data is collected or processed here."
            }
          </p>
          <p className="legal-text">
            {
              "The only personal data potentially processed directly through this website is information voluntarily provided by users who contact the publisher via email (for example, at ekynoxe@proton.me). Such data is used solely to respond to the inquiry and is not shared with third parties, unless required by law."
            }
          </p>
          <p className="legal-text">
            {
              "All payment, subscription, and age-verification data are processed externally by Fansly or other external platforms, under their own privacy policies and legal responsibilities. This website does not have access to, nor does it store, that data."
            }
          </p>
        </section>
        <section className="legal-section">
          <h2 className="legal-section-title">
            {"8. Changes to This Legal Notice"}
          </h2>
          <p className="legal-text">
            {
              "This Legal Notice may be updated from time to time to reflect changes in the website's operation or applicable law. The version published on this page at the time of your visit applies."
            }
          </p>
        </section>
        <section className="legal-section">
          <h2 className="legal-section-title">{"9. Contact"}</h2>
          <p className="legal-text">
            {
              "For any questions regarding this Legal Notice or the website, you may contact: ekynoxe@proton.me"
            }
          </p>
        </section>
      </section>
    </main>
  );
  if (isHome)
    return (
      <React.Fragment>
        <div className={`app${ageConfirmed ? "" : " app--blurred"}`}>
          <div className="top-bar">
            <button
              type="button"
              className="button button-secondary"
              onClick={toggleTheme}
            >
              {theme === "light" ? "Dark mode" : "Light mode"}
            </button>
          </div>
          <div className="hero-header">
            <h1 className="hero-title">{"EKYNOXE"}</h1>
          </div>
          <section
            className="hero-copy"
            aria-label="EKYNOXE comics introduction"
          >
            <p className="hero-kicker">
              {"EKYNOXE comics and free comic previews"}
            </p>
            <p className="hero-description">
              {"Looking for "}
              <strong>{"EKYNOXE comics"}</strong>
              {", an "}
              <strong>{"EKYNOXE comic"}</strong>
              {", or"}
              <strong>{" free comics"}</strong>
              {
                "? This online reader brings together free comic preview pages, long stories, and premium chapters in one place."
              }
            </p>
          </section>
          <div className="logo-marquee">
            <div className="logo-marquee-track">
              {socialLinks.map((j) => (
                <a
                  href={j.href}
                  className="logo-marquee-item"
                  aria-label={j.label}
                  key={j.id}
                >
                  <img
                    src={j.icon}
                    alt={j.label}
                    className="logo-marquee-icon"
                  />
                  <span className="logo-marquee-label">{j.label}</span>
                  <span className="logo-marquee-description">
                    {j.description}
                  </span>
                </a>
              ))}
            </div>
          </div>
          <main className="app-main app-main--home">
            <VideoPreviews
              releases={videoReleases}
              enabled={ageConfirmed}
              onOpenComic={openComic}
              hasFullAccess={hasFullAccess}
              onLockedClick={openAccess}
              onUnauthorized={onUnauthorized}
            />
            <LongStories stories={ci} onOpenStory={openComic} />
            <section
              className="home-section"
              aria-labelledby="latest-chapters-title"
            >
              <div className="home-section-heading">
                <p className="home-section-eyebrow">{"Chapter drops"}</p>
                <h2 id="latest-chapters-title" className="home-section-title">
                  {"Latest chapter releases"}
                </h2>
                <p className="home-section-description">
                  {
                    "New EKYNOXE comics, standalone chapters, and free comic updates released one chapter at a time."
                  }
                </p>
              </div>
              <ComicGrid
                comics={chapterReleases}
                nowMs={nowMs}
                onOpenComic={openComic}
              />
            </section>
            <section
              className="home-section"
              aria-labelledby="mini-stories-title"
            >
              <div className="home-section-heading">
                <p className="home-section-eyebrow">{"Short reads"}</p>
                <h2 id="mini-stories-title" className="home-section-title">
                  {"Mini stories"}
                </h2>
                <p className="home-section-description">
                  {"Short standalone comics, each complete in one release."}
                </p>
              </div>
              <ComicGrid
                comics={miniStories}
                nowMs={nowMs}
                onOpenComic={openComic}
              />
            </section>
          </main>
          <Footer onLegalNoticeClick={openLegal} />
        </div>
        {accessModalOpen && (
          <div className="access-modal-backdrop">
            <div className="access-modal">
              <h2 className="access-modal-title">{"Unlock full comics"}</h2>
              <p className="access-modal-text">
                {"Here’s how to get the password to unlock every page:"}
              </p>
              <ol className="access-modal-steps">
                <li className="access-modal-step">
                  <span className="access-modal-step-badge">{"1"}</span>
                  <span className="access-modal-step-text">
                    {"On"}{" "}
                    <a
                      href="https://fansly.com/EKYNOXE/posts"
                      target="_blank"
                      rel="noreferrer"
                      className="access-modal-link"
                    >
                      {"Fansly"}
                    </a>
                    {", subscribe to the "}
                    <strong>{"“Comic Reader”"}</strong>
                    {
                      " tier (if you haven't already). It's the only tier on the page, so you can't miss it."
                    }
                  </span>
                </li>
                <li className="access-modal-step">
                  <span className="access-modal-step-badge">{"2"}</span>
                  <span className="access-modal-step-text">
                    {"Check your "}
                    <strong>{"DMs"}</strong>
                    {" or the first "}
                    <strong>{"pinned post"}</strong>
                    {" on Fansly. It shows the "}
                    <strong>{"current password"}</strong>
                    {"."}
                  </span>
                </li>
                <li className="access-modal-step">
                  <span className="access-modal-step-badge">{"3"}</span>
                  <span className="access-modal-step-text">
                    {
                      "Type that password into the field below and confirm. In the reader, click "
                    }
                    <strong>{'"FREE MODE"'}</strong>
                    {" at the top right to open this popup again, then"}
                  </span>
                </li>
              </ol>
              <p className="access-modal-text">
                {
                  "If you think you should already have access but the password is not working or you never received it, please send me a message on Fansly so I can help."
                }
              </p>
              <form className="access-modal-form" onSubmit={submitPassword}>
                <label className="access-modal-label" htmlFor="access-code">
                  {"Fansly password"}
                </label>
                <input
                  id="access-code"
                  type="password"
                  className="access-modal-input"
                  value={passwordInput}
                  onChange={onPasswordChange}
                  autoComplete="off"
                />
                {accessError && (
                  <p className="access-modal-error">{accessError}</p>
                )}
                <div className="access-modal-actions">
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={closeAccess}
                  >
                    {"Cancel"}
                  </button>
                  <button type="submit" className="button">
                    {"Unlock"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {ageGate}
      </React.Fragment>
    );
  if (isLegal)
    return (
      <React.Fragment>
        <div className="app">
          <header className="app-header app-header--legal">
            <div className="reader-header-top">
              <button
                type="button"
                className="button button-secondary"
                onClick={goHome}
              >
                {"← Back to home"}
              </button>
            </div>
          </header>
          <LegalNotice />
          <Footer onLegalNoticeClick={openLegal} />
        </div>
        {ageGate}
      </React.Fragment>
    );
  const isFirstPage = route.page <= 1,
    isLastPage = comic ? route.page >= comic.pages : !1,
    isProtectedPage =
      comic != null && requiresProtectedSource(comic, route.page),
    visibleThumbnails =
      comic == null
        ? []
        : (() => {
            const j = [],
              G = comic.pages,
              de = Math.max(1, route.page - 3),
              Ye = Math.min(G, route.page + 3);
            for (let Mt = de; Mt <= Ye; Mt += 1) j.push(Mt);
            return j;
          })(),
    onPageClick = (j) => {
      if (!comic) return;
      const G = j.currentTarget.getBoundingClientRect(),
        Ye = j.clientX - G.left < G.width / 2;
      navigatePage(Ye ? "prev" : "next");
    },
    onPageHover = (j) => {
      const G = j.currentTarget.getBoundingClientRect(),
        Ye = j.clientX - G.left < G.width / 2;
      setHoverSide(Ye ? "left" : "right");
    },
    clearHover = () => {
      setHoverSide(null);
    };
  return (
    <React.Fragment>
      <div className={`app${ageConfirmed ? "" : " app--blurred"}`}>
        <header className="app-header app-header--reader">
          <div className="reader-header-top">
            <button
              type="button"
              className="button button-secondary reader-nav-button reader-nav-button--back"
              onClick={goHome}
            >
              {"← Back to home"}
            </button>
            <div className="reader-title-block">
              <h1 className="app-title app-title--reader">{comic?.name}</h1>
            </div>
            <button
              type="button"
              className="button button-secondary reader-nav-button reader-nav-button--theme"
              onClick={toggleTheme}
            >
              {theme === "light" ? "Dark mode" : "Light mode"}
            </button>
          </div>
          <div className="reader-header-bottom">
            <div className="reader-page-meta">
              <span className="reader-page-indicator">
                {"Page "}
                {route.page}
                {" / "}
                {comic?.pages}
              </span>
              {comic && isLongStory && (
                <QuickNavigation
                  currentPage={route.page}
                  totalPages={comic.pages}
                  onNavigate={goToPage}
                  key={`${comic.id}-${route.page}`}
                />
              )}
            </div>
            <div className="zoom-controls" aria-label="Comic zoom">
              <button
                type="button"
                className="button button-secondary zoom-button"
                onClick={() => changeZoom(-0.1)}
              >
                {"−"}
              </button>
              <span className="zoom-label">
                {Math.round(zoom * 100)}
                {"%"}
              </span>
              <button
                type="button"
                className="button button-secondary zoom-button"
                onClick={() => changeZoom(0.1)}
              >
                {"+"}
              </button>
            </div>
            <div className="orientation-toggle" aria-label="Mode de lecture">
              <button
                type="button"
                className={`button button-secondary orientation-button${route.orientation === "horizontal" ? " orientation-button--active" : ""}`}
                onClick={() => setOrientation("horizontal")}
              >
                {"Horizontal"}
              </button>
              <button
                type="button"
                className={`button button-secondary orientation-button${route.orientation === "vertical" ? " orientation-button--active" : ""}`}
                onClick={() => setOrientation("vertical")}
              >
                {"Vertical"}
              </button>
            </div>
            {hasPremiumPages && (
              <span
                className={`reader-mode-badge${isFreeMode ? " reader-mode-badge--free" : " reader-mode-badge--full"}`}
                onClick={isFreeMode ? openAccess : void 0}
                role={isFreeMode ? "button" : undefined}
                tabIndex={isFreeMode ? 0 : undefined}
                onKeyDown={isFreeMode ? event => { if(event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openAccess(); } } : undefined}
              >
                {isFreeMode ? "Free mode" : "Comic reader"}
              </span>
            )}
          </div>
        </header>
        <main className="app-main">
          {comic ? (
            <section
              className={`reader reader--${route.orientation}`}
              aria-label={`Reading ${comic.name}`}
            >
              <div className="reader-main">
                {route.orientation === "horizontal" ? (
                  <React.Fragment>
                    <div
                      className={`reader-image-container${hoverSide === "left" ? " reader-image-container--hover-left" : ""}${hoverSide === "right" ? " reader-image-container--hover-right" : ""}`}
                      onContextMenu={(j) => j.preventDefault()}
                      onClick={onPageClick}
                      onMouseMove={onPageHover}
                      onMouseLeave={clearHover}
                      style={{
                        width: 900 * zoom,
                      }}
                    >
                      {comic &&
                      isPageLocked(comic, route.page, hasFullAccess) ? (
                        <button
                          type="button"
                          className="reader-locked reader-locked--emoji"
                          onClick={event => { event.stopPropagation(); openAccess(); }}
                        >
                          <img
                            src={lockedImageUrl()}
                            alt="Locked page – click to unlock"
                            className="reader-locked-preview"
                          />
                        </button>
                      ) : comic ? (
                        (() => {
                          if (hasFullAccess && isProtectedPage) {
                            const j = protectedPageSource(comic, route.page);
                            return (
                              <ProtectedPage
                                comicId={j.comicId}
                                page={j.page}
                                alt={`Page ${route.page} of ${comic.name}`}
                                className="reader-image"
                                onUnauthorized={onUnauthorized}
                              />
                            );
                          }
                          return (
                            <img
                              src={publicPageUrl(comic, route.page)}
                              alt={`Page ${route.page} of ${comic.name}`}
                              className="reader-image"
                            />
                          );
                        })()
                      ) : null}
                    </div>
                    <div className="reader-thumbnails reader-thumbnails--horizontal">
                      {visibleThumbnails.map((j) => (
                        <button
                          type="button"
                          className={`thumbnail-page${j === route.page ? " thumbnail-page--active" : ""}${comic && isPageLocked(comic, j, hasFullAccess) ? " thumbnail-page--locked" : ""}`}
                          onClick={() => navigatePage(j)}
                          disabled={j === route.page}
                          key={j}
                        >
                          <span className="thumbnail-page-number">{j}</span>
                          {comic && isPageLocked(comic, j, hasFullAccess) ? (
                            <img
                              src={lockedImageUrl()}
                              alt={`Locked thumbnail page ${j}`}
                              className="thumbnail-image"
                            />
                          ) : comic &&
                            requiresProtectedSource(comic, j) &&
                            hasFullAccess ? (
                            (() => {
                              const G = protectedPageSource(comic, j);
                              return (
                                <ProtectedPage
                                  comicId={G.comicId}
                                  page={G.page}
                                  alt={`Thumbnail page ${j}`}
                                  className="thumbnail-image"
                                  onUnauthorized={onUnauthorized}
                                />
                              );
                            })()
                          ) : (
                            <img
                              src={publicPageUrl(comic, j)}
                              alt={`Thumbnail page ${j}`}
                              className="thumbnail-image"
                            />
                          )}
                        </button>
                      ))}
                    </div>
                    <div className="reader-controls">
                      <button
                        type="button"
                        className="button"
                        onClick={() => navigatePage("prev")}
                        disabled={isFirstPage}
                      >
                        {"← Previous page"}
                      </button>
                      <span className="reader-page-indicator">
                        {"Page "}
                        {route.page}
                        {" / "}
                        {comic.pages}
                      </span>
                      <button
                        type="button"
                        className="button"
                        onClick={() => navigatePage("next")}
                        disabled={isLastPage}
                      >
                        {"Next page →"}
                      </button>
                    </div>
                  </React.Fragment>
                ) : (
                  <div
                    ref={verticalReaderRef}
                    className="reader-vertical-scroll"
                    style={{
                      width: `${900 * zoom}px`,
                      maxWidth: "100%",
                    }}
                  >
                    {Array.from(
                      {
                        length: comic.pages,
                      },
                      (j, G) => {
                        const de = G + 1;
                        return (
                          <VerticalPage
                            comic={comic}
                            pageNumber={de}
                            hasFullAccess={hasFullAccess}
                            onUnauthorized={onUnauthorized}
                            onLockedClick={openAccess}
                            key={de}
                          />
                        );
                      },
                    )}
                  </div>
                )}
              </div>
            </section>
          ) : (
            <div className="reader-message">
              <p>{"This comic does not exist or is not available."}</p>
              <button type="button" className="button" onClick={goHome}>
                {"Back to list"}
              </button>
            </div>
          )}
        </main>
        <Footer onLegalNoticeClick={openLegal} />
      </div>
      {accessModalOpen && (
        <div className="access-modal-backdrop">
          <div className="access-modal">
            <h2 className="access-modal-title">{"Unlock full access"}</h2>
            <p className="access-modal-text">
              {
                "Here’s how to get the password to unlock every page and download:"
              }
            </p>
            <ol className="access-modal-steps">
              <li className="access-modal-step">
                <span className="access-modal-step-badge">{"1"}</span>
                <span className="access-modal-step-text">
                  {"On"}{" "}
                  <a
                    href="https://fansly.com/EKYNOXE/posts"
                    target="_blank"
                    rel="noreferrer"
                    className="access-modal-link"
                  >
                    {"Fansly"}
                  </a>
                  {", subscribe to the "}
                  <strong>{"“Comic Reader”"}</strong>
                  {
                    " tier (if you haven't already). It's the only tier on the page, so you can't miss it."
                  }
                </span>
              </li>
              <li className="access-modal-step">
                <span className="access-modal-step-badge">{"2"}</span>
                <span className="access-modal-step-text">
                  {"Check your "}
                  <strong>{"DMs"}</strong>
                  {" or the first "}
                  <strong>{"pinned post"}</strong>
                  {" on Fansly. It shows the "}
                  <strong>{"current password"}</strong>
                  {"."}
                </span>
              </li>
              <li className="access-modal-step">
                <span className="access-modal-step-badge">{"3"}</span>
                <span className="access-modal-step-text">
                  {"Type that password into the field below and clic "}
                  <strong>{"Unlock"}</strong>
                  {"."}
                </span>
              </li>
            </ol>
            <p className="access-modal-text">
              {
                "PS: password will always be short and easy to remember, but it will change with every new comic drop (about twice a month)."
              }
            </p>
            <p className="access-modal-text">
              {
                "If you think you should already have access but the password is not working or you never received it, please send me a message on Fansly so I can help."
              }
            </p>
            <form className="access-modal-form" onSubmit={submitPassword}>
              <label
                className="access-modal-label"
                htmlFor="access-code-reader"
              >
                {"Fansly password"}
              </label>
              <input
                id="access-code-reader"
                type="password"
                className="access-modal-input"
                value={passwordInput}
                onChange={onPasswordChange}
                autoComplete="off"
              />
              {accessError && (
                <p className="access-modal-error">{accessError}</p>
              )}
              <div className="access-modal-actions">
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={closeAccess}
                >
                  {"Cancel"}
                </button>
                <button type="submit" className="button">
                  {"Unlock"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {ageGate}
    </React.Fragment>
  );
}
